import { MessageListItem } from '../types';
import { isMessage } from './ItemType';

/**
 * Determine if the article body needs to be trimmed. This utility also includes empty message information.
 */
export function previewDetails (body: string, { previewOffset = 75, trimOffset = previewOffset, defaultBody = '<em>No information available</em>' }: { previewOffset?: number; trimOffset?: number; defaultBody?: string } = {}) {
  if (!body || !(body || '').trim()) {
    return {
      isPreview: false,
      isEmpty: true,
      body: defaultBody,
      preview: '',
    };
  }
  const doc = new DOMParser().parseFromString(body, 'text/html');
  const textOnly = (doc.body.textContent || doc.body.innerText || '').trim();
  const isEmpty = !textOnly.length;
  const isPreview = textOnly.length > previewOffset || doc.querySelector('ol,ul');
  return {
    isPreview,
    isEmpty,
    body: isEmpty ? defaultBody : body,
    preview: isPreview ? `${textOnly.substring(0, trimOffset)}...` : '',
  };
}

/**
 * Copied from the `RuleEngineHelper.vue`.
 */
const TransactionType = {
  Deposit: 1,
  Withdrawal: 2,
  Bet: 3,
} as const;

export function getActionKeyFromCategoryName (name: string) {
  const cleanName = (name || '').trim().toLowerCase();
  switch (cleanName) {
    case 'deposits':
      return TransactionType.Deposit;
    case 'withdrawals':
      return TransactionType.Withdrawal;
    case 'bets and cashout':
    case 'bets':
      return TransactionType.Bet;
    default:
      return 0;
  }
}

/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 */
export function getRandomInt (min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const charactersLength = characters.length;

/**
 * Generate a random string.
 * @see https://stackoverflow.com/a/1349426
 * @param length The length of the random string. (Default 10)
 */
export function randomId (length = 10) {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function randomUUID () {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

interface ToString extends Pick<String, 'toString'> {}

/**
 * Format a string from the given list of arguments.
 * #### Example
 * ```ts
 * console.log(formatString('Hello {0}!, Good {1}', 'World', 'Morning'));
 * // logs: Hello World!, Good Morning.
 *
 * console.log(formatString('Hello {name}, Welcome to {company}', {name: 'Bob', company: 'Pleased'}))
 * // logs: Hello Bob, Welcome to Pleased.
 * ```
 * @param input The input string
 * @param format A list of values to format, can be a object, or a list of arguments.
 */
export function formatString (input: string | ToString, ...format: Array<string|number|ToString>): string;
export function formatString (input: string | ToString, formatDict: Record<string|number, string|number|ToString>): string;
export function formatString (input: string | ToString, ...format: any[]) {
  let str = input.toString();
  if (format.length) {
    let t = typeof format[0];
    let key;
    let args = (t === 'string' || t === 'number' || t === 'boolean')
      ? Array.prototype.slice.call(format)
      : format[0];

    for (key in args) {
      str = str.replace(new RegExp('\\{' + key + '\\}', 'gi'), args[key]);
    }
  }

  return str;
};

/**
 * Check if the given items is a message or has any messages.
 */
export function hasAnyMessages (store: MessageListItem|MessageListItem[]|null) {
  if (!store) return false;
  return Array.isArray(store) ? !!store.length && store.some(m => isMessage(m)) : isMessage(store);
}
